import { CaretDown } from '@app/components/icons/caret-down';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { Listbox, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import type { TFunction } from 'i18next';
import React, { Fragment, useCallback } from 'react';

import type { BrandingPreset } from '../../domain';
import { usePresetColors, useRecordingAnalytics } from '../../hooks';
import { selectSelectedClip } from '../../selector';
import { ColorLabels } from '../../theme/constants';
import { ThemeCard } from './theme-card';

type PresetSelectorProps = {
  disabled?: boolean;
};

export const PresetSelector = ({ disabled = false }: PresetSelectorProps) => {
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();

  const { status, presets } = useAppSelector(
    state => state.contentHub.recordingInfo.colorPresets,
  );
  const { currentPreset } = usePresetColors();
  const selectedClip = useAppSelector(selectSelectedClip);
  const dispatch = useAppDispatch();

  const handleChange = useCallback(
    (presetId: string) => {
      // Only dispatch if a clip is selected
      if (!selectedClip) return;

      const preset = presets.find(p => p.id === presetId);

      if (!preset) return;

      dispatch({
        type: '[recording] colour preset changed',
        payload: {
          preset,
        },
      });
      trackRecordingEvent('ch_creator_lab_appearance_tab_brand_selected', {
        brandName: getPresetName(t, preset),
      });
    },
    [presets, dispatch, selectedClip, trackRecordingEvent, t],
  );

  if (status === 'processing' || status === 'idle')
    return (
      <div className="relative w-full">
        <div className="h-[68px] w-full animate-pulse rounded-[10px] bg-spring-neutral-b0/10 border border-solid border-spring-neutral-b0/20"></div>
      </div>
    );

  if (!currentPreset || presets.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="relative w-full">
        <Listbox
          value={currentPreset?.id}
          onChange={handleChange}
          disabled={disabled}
        >
          {({ open }) => (
            <>
              <Listbox.Button
                id="color-preset-selector"
                className={cx(
                  'flex w-full flex-row justify-between items-center min-h-[68px] border border-solid border-spring-neutral-b0/20 text-white rounded-[10px] hover:bg-spring-neutral-b0/10 transition-colors',
                  'focus:outline-none',
                  {
                    'bg-spring-neutral-b0/10': open,
                  },
                )}
                aria-labelledby="color-preset-selector"
              >
                <div className="flex flex-1 items-center gap-2 px-4 py-1">
                  <ThemeCard
                    name={getPresetName(t, currentPreset)}
                    isDefault={currentPreset.isDefault}
                    primary={
                      currentPreset?.config[
                        ColorLabels['BRAND-PRIMARY']
                      ] as string
                    }
                    background={
                      currentPreset?.config[
                        ColorLabels['NEUTRAL-PRIMARY']
                      ] as string
                    }
                    text={
                      currentPreset?.config[
                        ColorLabels['DARK-SECONDARY']
                      ] as string
                    }
                  />
                </div>
                <div className="pr-3 flex-shrink-0">
                  <CaretDown
                    className={cx('size-5 transition-transform duration-200', {
                      'rotate-180': open,
                    })}
                  />
                </div>
              </Listbox.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Listbox.Options className="absolute left-0 mt-2 w-full origin-top-left rounded-lg bg-spring-neutral-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-spring-neutral-b0/20 z-50 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    {presets.map(preset => (
                      <Listbox.Option
                        key={preset.id}
                        value={preset.id}
                        className={({ active }) =>
                          cx(
                            'group flex flex-col w-full items-start gap-1 rounded-md px-3 py-2.5 text-[14px] font-medium text-white leading-[20px] tracking-[0px] hover:bg-spring-neutral-b0/10 transition-colors cursor-pointer',
                            {
                              'bg-spring-neutral-b0/10': active,
                            },
                          )
                        }
                      >
                        <ThemeCard
                          name={getPresetName(t, preset)}
                          isDefault={preset.isDefault}
                          primary={
                            preset.config[
                              ColorLabels['BRAND-PRIMARY']
                            ] as string
                          }
                          background={
                            preset.config[
                              ColorLabels['NEUTRAL-PRIMARY']
                            ] as string
                          }
                          text={
                            preset.config[
                              ColorLabels['DARK-SECONDARY']
                            ] as string
                          }
                        />
                      </Listbox.Option>
                    ))}
                  </div>
                </Listbox.Options>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
    </div>
  );
};

function getPresetName(t: TFunction, preset: BrandingPreset) {
  return preset.isPredefined
    ? t(`content-recording.appearance.color-presets.names.${preset.id}`)
    : preset.name;
}
